(function () {
  function solidColorDataUrl(color) {
    const c = document.createElement("canvas");
    c.width = 2;
    c.height = 2;
    const ctx = c.getContext("2d");
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, 2, 2);
    return c.toDataURL();
  }

  function initStoreLocator(section) {
    const sectionData = section.dataset;
    const autoRotate = sectionData.autoRotate !== "false";
    const isVector = sectionData.isVector === "true";
    const vectorSeaColor = sectionData.vectorSeaColor ?? "#7b97d4";
    const vectorCountryColor = sectionData.vectorCountryColor ?? "#d6c68e";
    const vectorBorderColor = sectionData.vectorBorderColor ?? "#b4a984";
    const polygonAltitude = parseFloat(sectionData.polygonAltitude) || 0.01;
    const globeTextureUrl = sectionData.globeTextureUrl ?? "";
    const globeScriptUrl = sectionData.globeScriptUrl ?? "";
    const countriesUrl = sectionData.countriesUrl ?? "";

    const locationsEl = section.querySelector(".store-locator__locations-data");
    const locations = locationsEl ? JSON.parse(locationsEl.textContent) : [];

    const pinTemplate = section.querySelector(".store-locator__pin-template");
    const pinIconHtml = pinTemplate
      ? pinTemplate.innerHTML.trim()
      : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="store-locator__globe-pin-icon"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>';

    const pinEls = [];
    let globeInstance = null;
    let lockedIndex = null;
    let cachedRows = [];

    function setActive(index) {
      pinEls.forEach((el, i) => {
        if (!el) return;
        el.classList.toggle("is-active", i === index);
        el.classList.toggle("is-inactive", i !== index);
      });
      cachedRows.forEach((r, i) => {
        r.classList.toggle("is-active", i === index);
        r.classList.toggle("is-inactive", i !== index);
      });
    }

    function clearActive() {
      if (lockedIndex !== null) {
        setActive(lockedIndex);
      } else {
        pinEls.forEach(
          (el) => el && el.classList.remove("is-active", "is-inactive"),
        );
        cachedRows.forEach((r) => r.classList.remove("is-active", "is-inactive"));
      }
    }

    function lockActive(index) {
      if (lockedIndex === index) {
        lockedIndex = null;
        pinEls.forEach(
          (el) => el && el.classList.remove("is-active", "is-inactive"),
        );
        cachedRows.forEach((r) => r.classList.remove("is-active", "is-inactive"));
      } else {
        lockedIndex = index;
        setActive(index);
      }
    }

    function initGlobe(countriesData) {
      const mapEl = section.querySelector(".store-locator__map");
      const mapWrap = section.querySelector(".store-locator__map-wrap");
      if (!mapEl || !window.Globe) return;

      // Abort all listeners from any previous init, even across re-init closures
      if (section._storeLocatorController) section._storeLocatorController.abort();
      const controller = new AbortController();
      section._storeLocatorController = controller;
      const { signal } = controller;

      mapEl.innerHTML = "";
      globeInstance = null;
      mapWrap?.classList.remove("is-loaded");
      cachedRows = Array.from(section.querySelectorAll(".store-locator__row"));

      const zoomSlider = section.querySelector(".store-locator__zoom-slider");
      const setPointOfView = (pov, transitionMs = 800) => {
        globeInstance?.pointOfView(
          {
            ...pov,
            altitude: zoomSlider
              ? 4 - (parseFloat(zoomSlider.value) / 100) * 3.5
              : 1.8,
          },
          transitionMs,
        );
      };

      globeInstance = Globe()
        .width(mapEl.clientWidth)
        .height(mapEl.clientWidth)
        .globeImageUrl(
          isVector ? solidColorDataUrl(vectorSeaColor) : globeTextureUrl,
        )
        .backgroundColor("rgba(0,0,0,0)")
        .showAtmosphere(true)
        .atmosphereColor("#fff")
        .polygonsData(countriesData || [])
        .polygonAltitude(polygonAltitude)
        .polygonCapColor(() =>
          isVector ? vectorCountryColor : "rgba(180,180,180,0.15)",
        )
        .polygonSideColor(() =>
          isVector ? vectorBorderColor : "rgba(180,180,180,0.05)",
        )
        .polygonStrokeColor(() =>
          isVector ? vectorBorderColor : "rgba(180,180,180,0.4)",
        )
        .htmlElementsData(locations)
        .htmlLat((d) => d.lat)
        .htmlLng((d) => d.lng)
        .htmlAltitude(0.01)
        .htmlElement((d) => {
          const el = document.createElement("div");
          el.className = "store-locator__globe-pin";
          el.innerHTML = pinIconHtml;
          pinEls[d.index] = el;

          el.addEventListener("mouseenter", () => setActive(d.index));
          el.addEventListener("mouseleave", clearActive);
          el.addEventListener("click", () => {
            lockActive(d.index);
            setPointOfView(d);
            const row = cachedRows[d.index];
            if (row)
              row.scrollIntoView({ behavior: "smooth", block: "center" });
          });

          return el;
        })(mapEl);

      globeInstance.onGlobeReady(() => mapWrap?.classList.add("is-loaded"));

      globeInstance.controls().autoRotate = autoRotate;
      globeInstance.controls().autoRotateSpeed = -0.4;
      globeInstance.controls().enableZoom = false;

      const lights = globeInstance.lights();
      for (const light of Object.values(lights)) {
        if (light.type === "DirectionalLight") light.intensity = 0;
        if (light.type === "AmbientLight") light.intensity = 4.25;
      }

      mapEl.addEventListener("mouseenter", () => {
        globeInstance.controls().autoRotate = false;
      }, { signal });
      mapEl.addEventListener("mouseleave", () => {
        if (lockedIndex === null)
          globeInstance.controls().autoRotate = autoRotate;
      }, { signal });

      const ro = new ResizeObserver(() => {
        globeInstance.width(mapEl.clientWidth).height(mapEl.clientWidth);
      });
      ro.observe(mapEl);
      signal.addEventListener("abort", () => ro.disconnect());

      if (locations.length === 1) {
        setPointOfView(locations[0], 0);
      } else {
        setPointOfView({ lat: 38, lng: -97 }, 0); // start over USA
      }

      if (zoomSlider) {
        const initialPov = globeInstance.pointOfView();
        setPointOfView(initialPov);
        zoomSlider.addEventListener("input", () => {
          const pov = globeInstance.pointOfView();
          setPointOfView(pov);
        }, { signal });
      }

      cachedRows.forEach((row, i) => {
        row.addEventListener("click", () => {
          lockActive(i);
          if (locations[i]) {
            globeInstance.controls().autoRotate = false;
            setPointOfView(locations[i]);
          }
          if (lockedIndex === null)
            globeInstance.controls().autoRotate = autoRotate;
        }, { signal });
      });
    }

    function loadGlobe() {
      const mapEl = section.querySelector(".store-locator__map");

      function doLoad() {
        const init = (countriesData) => initGlobe(countriesData);
        const fetchThenInit = () => {
          if (isVector && countriesUrl) {
            fetch(countriesUrl)
              .then((r) => r.json())
              .then((data) => init(data.features))
              .catch(() => init());
          } else {
            init();
          }
        };
        if (window.Globe) {
          fetchThenInit();
          return;
        }
        const script = document.createElement("script");
        script.src = globeScriptUrl;
        script.onload = fetchThenInit;
        document.head.appendChild(script);
      }

      // If the map element already has dimensions, load immediately.
      // Otherwise wait for layout — on hot-reload, globe.gl (cached) can resolve
      // before section CSS (cache miss) is applied, causing a 0×0 canvas.
      if (!mapEl || mapEl.clientWidth > 0) {
        doLoad();
        return;
      }

      const ro = new ResizeObserver(() => {
        if (mapEl.clientWidth > 0) {
          ro.disconnect();
          doLoad();
        }
      });
      ro.observe(mapEl);
    }

    loadGlobe();

    // Search
    const searchInput = section.querySelector(".store-locator__search-input");
    const noResults = section.querySelector(".store-locator__no-results");

    if (searchInput) {
      searchInput.addEventListener("input", () => {
        const q = searchInput.value.trim().toLowerCase();
        let visible = 0;

        cachedRows.forEach((card, i) => {
          const haystack = (card.dataset.search || "").toLowerCase();
          const show = !q || haystack.includes(q);
          card.hidden = !show;
          if (pinEls[i]) pinEls[i].style.visibility = show ? "" : "hidden";
          if (show) visible++;
        });

        if (noResults) noResults.hidden = visible > 0;
      });
    }

    return {
      reload: loadGlobe,
    };
  }

  // Boot all instances on page load
  function bootAll() {
    document
      .querySelectorAll(".store-locator[data-section-id]")
      .forEach(initStoreLocator);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll);
  } else {
    bootAll();
  }

  // Shopify theme editor re-init
  document.addEventListener("shopify:section:load", (e) => {
    const section = document.querySelector(
      `.store-locator[data-section-id="${e.detail.sectionId}"]`,
    );
    if (section) {
      initStoreLocator(section);
    }
  });
})();
